import { BrowserModule, Title } from "@angular/platform-browser";
import { NgModule } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { NgRedux, NgReduxModule } from "@angular-redux/store";
import { HttpModule } from "@angular/http";
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpClientModule,
} from "@angular/common/http/";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";

import { AppRoutingModule } from "./app-routing.module";
import { jqxDataTableComponent } from "../assets/jqwidgets-ts/angular_jqxdatatable";
import { jqxGridComponent } from "../assets/jqwidgets-ts/angular_jqxgrid";
import { jqxWindowComponent } from "../assets/jqwidgets-ts/angular_jqxwindow";

import { AppComponent } from "./app.component";
import { SiteComponent } from "./components/site/site.component";
import { NavbarComponent } from "./components/navbar/navbar.component";
import { SidenavComponent } from "./components/sidenav/sidenav.component";

import { NotFoundComponent } from "./pages/404/404.component";

import { HedgeBranch } from "./pages/exposure/hedge/hedge.component";
import { FundBranch } from "./pages/exposure/fund/fund.component";
import { AccExpHedgeBranch } from "./pages/exposure/acc-exp-hedge/acc-exp-hedge.component";
import { AccExpFundBranch } from "./pages/exposure/acc-exp-fund/acc-exp-fund.component";

import { TimingBranch } from "./pages/rules/timing/timing.component";
import { NettingBranch } from "./pages/rules/netting/netting.component";
import { BrokerSelectionBranch } from "./pages/rules/broker-selection/broker-selection.component";

import { OpenFXBranch } from "./pages/orders/openfx/openfx.component";
import { HedgeTradesBranch } from "./pages/orders/hedge-trades/hedge-trades.component";

import { FXOrdersHistory } from "./pages/history/fx-orders-h/fx-orders-h.component";
import { SODCashHistory } from "./pages/history/sod-cash-h/sod-cash-h.component";
import { SODForwardsHistory } from "./pages/history/sod-forwards-h/sod-forwards-h.component";
import { SODAssetsHistory } from "./pages/history/sod-assets-h/sod-assets-h.component";
import { AssetTradesHistory } from "./pages/history/asset-trades-h/asset-trades-h.component";
import { SubFXOrdersHistory } from "./pages/history/sub-fx-orders-h/sub-fx-orders-h.component";
import { FXOrderRecommendationsHistory } from "./pages/history/fx-recommendations-h/fx-recommendations-h.component";
import { InternalAllocationsHistory } from "./pages/history/int-allocations-h/int-allocations-h.component";
import { TimingHistory } from "./pages/history/timing-h/timing-h.component";
import { NettingHistory } from "./pages/history/netting-h/netting-h.component";
import { BrokerSelectionHistory } from "./pages/history/broker-h/broker-h.component";
import { OrganizationsHistory } from "./pages/history/org-h/org-h.component";
import { UsersHistory } from "./pages/history/users-h/users-h.component";
import { AccountsHistory } from "./pages/history/accounts-h/accounts-h.component";
import { AccountGroupsHistory } from "./pages/history/account-groups-h/account-groups-h.component";
import { FXBrokersHistory } from "./pages/history/fx-brokers-h/fx-brokers-h.component";
import { CurrencyGroupsHistory } from "./pages/history/currency-groups-h/currency-groups-h.component";
import { SystemIntegrationHistory } from "./pages/history/system-int-h/system-int-h.component";

import { OrganizationsBranch } from "./pages/settings/organizations/organizations.component";
import { AccountBranch } from "./pages/settings/account/account.component";
import { AccountGroupsBranch } from "./pages/settings/account-groups/account-groups.component";
import { MyProfileBranch } from "./pages/settings/myprofile/myprofile.component";
import { UsersBranch } from "./pages/settings/users/users.component";
import { FXBrokersBranch } from "./pages/settings/fx-brokers/fx-brokers.component";
import { CurrencyGroupsBranch } from "./pages/settings/currency-groups/currency-groups.component";
import { SystemIntegrationBranch } from "./pages/settings/system-integration/system-integration.component";

import { LoginPage } from "./pages/login/login.component";

import { OrganizationBranch } from "./pages/organization/organization.component";

import { AuthGuard } from "./guards/auth.guard";
import { _UserService } from "./services/user.service";
import { _UsersService } from "./services/users.service";

import { _ReferenceDataService } from "./services/referenceData.service";
import { _OrganizationService } from "./services/organization.service";
import { _GlobalConstants } from "./services/globalConstants.service";
import { _AuthenticationService } from "./services/auth.service";
import { _RuleService } from "./ng1-services/rule.service";
import { _RuleConditionService } from "./ng1-services/rule-condition.service";
import { _Ng1UserService } from "./ng1-services/user.service";
import { _GridService } from "./ng1-services/grid.service";

import { ForbiddenHttpInterceptor } from "./interceptors/http.interceptor";

import { IAppState } from "./interfaces/appstate.interface";
import { PermissionActions } from "./app.actions";
import { INITIAL_STATE, rootReducer } from "../store";
import { TargetsBranch } from "./pages/rules/targets/targets.component";
import { _RuleCurrencyCrossService } from "./ng1-services/rule-currency-cross.service";
import { _SettingsService } from "./services/settings.service";
import { MinimumOrderQuantityBranch } from "./pages/settings/minimum-order-quantity/minimum-order-quantity.component";
import { _RuleNdfCurrencyService } from "./ng1-services/rule-ndf-currency.service";
import { _OrganizationHolidayService } from "./services/organization.holiday.service";
import { OrganizationHolidayBranch } from "./pages/settings/holidays/organization.holidays.component";
import { SecurityQuantityRatioBranch } from "./pages/settings/security-quantity-ratio/security-quantity-ratio.component";
import { _RuleNettingPriorityService } from "./services/ruleNettingPriority.service";
import { _RuleLiquidityProviderService } from "./services/ruleLiquidityProvider.service";
import { _RuleHedgeRatioService } from "./services/ruleHedgeRatio.service";
import { _TradingAccountService } from "./services/tradingAccount.service";
import { NotificationChangePassword } from "./pages/notification/change-password/change-password.component";
import { _NotificationService } from "./services/notification.service";
import { ForgotPasswordPage } from "./pages/forgot-password/forgot-password.component";

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

@NgModule({
  declarations: [
    AppComponent,
    SiteComponent,
    NavbarComponent,
    SidenavComponent,

    OrganizationBranch,

    NotFoundComponent,

    HedgeBranch,
    FundBranch,
    AccExpHedgeBranch,
    AccExpFundBranch,

    TimingBranch,
    NettingBranch,
    TargetsBranch,
    BrokerSelectionBranch,

    OpenFXBranch,
    HedgeTradesBranch,

    FXOrdersHistory,
    SODCashHistory,
    SODForwardsHistory,
    SODAssetsHistory,
    AssetTradesHistory,
    SubFXOrdersHistory,
    FXOrderRecommendationsHistory,
    InternalAllocationsHistory,
    TimingHistory,
    NettingHistory,
    BrokerSelectionHistory,
    OrganizationsHistory,
    UsersHistory,
    AccountsHistory,
    AccountGroupsHistory,
    FXBrokersHistory,
    CurrencyGroupsHistory,
    SystemIntegrationHistory,

    NotificationChangePassword,

    AccountBranch,
    AccountGroupsBranch,
    MyProfileBranch,
    OrganizationsBranch,
    UsersBranch,
    FXBrokersBranch,
    OrganizationHolidayBranch,
    SecurityQuantityRatioBranch,
    MinimumOrderQuantityBranch,
    CurrencyGroupsBranch,
    SystemIntegrationBranch,

    ForgotPasswordPage,
    LoginPage,
    jqxDataTableComponent,
    jqxGridComponent,
    jqxWindowComponent,
  ],
  imports: [
    BrowserModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    FormsModule,
    HttpModule,
    HttpClientModule,
    AppRoutingModule,
    NgReduxModule,
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ForbiddenHttpInterceptor,
      multi: true,
    },
    AuthGuard,
    PermissionActions,
    Title,
    _AuthenticationService,
    _UserService,
    _UsersService,
    _GlobalConstants,
    _ReferenceDataService,
    _OrganizationService,
    _OrganizationHolidayService,
    _GridService,
    _RuleService,
    _SettingsService,
    _Ng1UserService,
    _RuleConditionService,
    _RuleCurrencyCrossService,
    _RuleNdfCurrencyService,
    _RuleHedgeRatioService,
    _RuleNettingPriorityService,
    _TradingAccountService,
    _RuleLiquidityProviderService,
    _NotificationService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(ngRedux: NgRedux<IAppState>) {
    // Tell @angular-redux/store about our rootReducer and our initial state.
    // It will use this to create a redux store for us and wire up all the
    // events.
    ngRedux.configureStore(rootReducer, INITIAL_STATE);
  }
}



// WEBPACK FOOTER //
// /app/wiserisk-core/src/main/ng4app/src/app/app.module.ts